/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import {
  findBy,
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { tagName } from '@ember-decorators/component';
import { modifier } from 'ember-modifier';
import {
  barSegments,
  defaultIsInterim,
  percentOf,
  rulesVerb,
  thresholdFor,
} from 'linced-app/utils/progress-overview';

// The line labels sit in rows beneath their bar: the first this far below it,
// each further row this much lower, and labels sharing a row at least this
// far apart.
const MARK_LABEL_TOP = 8;
const MARK_LABEL_ROW = 30;
const MARK_LABEL_GAP = 8;

// placeMarkLabels lays out a bar's line labels. Each starts at its line and
// they share a row, unless one would run into another, which then drops to
// the next row down. A label that would run past the bar's right end ends at
// its line instead.
function placeMarkLabels(element) {
  let width = element.clientWidth;
  let rows = [];
  Array.from(element.querySelectorAll('[data-mark-label]'))
    .map((label) => {
      label.style.transform = '';
      let left = label.offsetLeft;
      let size = label.offsetWidth;
      if (left + size > width) {
        label.style.transform = 'translateX(-100%)';
        left -= size;
      }
      return { label, left, right: left + size };
    })
    .sort((a, b) => a.left - b.left)
    .forEach((entry) => {
      let row = rows.findIndex((right) => entry.left >= right + MARK_LABEL_GAP);
      if (row < 0) {
        row = rows.length;
        rows.push(0);
      }
      rows[row] = entry.right;
      entry.label.style.top = `${MARK_LABEL_TOP + row * MARK_LABEL_ROW}px`;
    });
  element.style.height = `${MARK_LABEL_TOP + rows.length * MARK_LABEL_ROW}px`;
}

/**
 * Insights::BenchmarkPosition shows where the on-track line falls for a year's interim
 * and full Milestones, over the same five descriptors.
 *
 * The point of the pairing is that the two bars are identical apart from where
 * the line sits: a band can be on the passing side of one and the failing side
 * of the other, so a cohort's headline can fall between them without anyone
 * having gone backwards.
 *
 * @param {Ember.Array} data - the All Students rows from the progress overview
 *   controller (judgement rows plus the total/percentage summaries).
 * @param {string} selectedYear - the page's chosen year.
 * @param {{interim: number, full: number}} onTrackGrades - the judgement a
 *   learner must reach for each kind of Milestone.
 * @param {{mid: string, end: string}} labels - what the two slots of a year
 *   are called in the chart's mode.
 */
@tagName('')
export default class BenchmarkPosition extends Component {
  /** @type {Ember.Array|null} */
  data = null;

  /** @type {{interim: number, full: number}|null} */
  onTrackGrades = null;

  /** @type {{mid: string, end: string}|null} */
  labels = null;

  /** @type {string|null} */
  selectedYear = null;

  /** @type {Function} Called with (year, half, judgement) on a segment click. */
  onSelect() {}

  /** @type {boolean} Whether the explanation panel is showing. */
  @tracked showExplanation = false;

  // Lowest descriptor first, so the bars read left to right the way the levels
  // do and the passing side of the line sits on the right.
  @computed('data.[]')
  get judgementRows() {
    let data = this.data || [];
    return sortBy(rejectBy(data, 'isSummary'), 'key');
  }

  // The key to the bars' colours: the segments show only their counts.
  @computed('judgementRows.[]')
  get legend() {
    return this.judgementRows.map((row) => ({
      label: row.label,
      swatchStyle: row.swatchStyle,
    }));
  }

  @computed(
    'data.[]',
    'labels.mid',
    'onTrackGrades.{full,interim}',
    'selectedYear',
  )
  get midRow() {
    return this._row('mid');
  }

  @computed(
    'data.[]',
    'labels.end',
    'onTrackGrades.{full,interim}',
    'selectedYear',
  )
  get endRow() {
    return this._row('end');
  }

  @computed('midRow.mixed', 'endRow.mixed')
  get anyMixed() {
    return !!(this.midRow?.mixed || this.endRow?.mixed);
  }

  // The descriptors that sit on the passing side of one line but not the
  // other. These are what "changes sides" between the two bars.
  @computed('judgementRows.[]', 'onTrackGrades.{full,interim}')
  get movingBands() {
    let interim = this.onTrackGrades?.interim;
    let full = this.onTrackGrades?.full;
    let lower = Math.min(interim, full);
    let upper = Math.max(interim, full);
    return this.judgementRows
      .filter((row) => row.key >= lower && row.key < upper)
      .map((row) => row.label);
  }

  @computed('movingBands.[]')
  get movingBandsLabel() {
    let bands = this.movingBands;
    if (isEmpty(bands)) {
      return null;
    }
    if (bands.length === 1) {
      return bands[0];
    }
    return `${bands.slice(0, -1).join(', ')} and ${bands[bands.length - 1]}`;
  }

  @computed('judgementRows.[]', 'onTrackGrades.interim')
  get interimGradeLabel() {
    return this._gradeLabel(this.onTrackGrades?.interim);
  }

  @computed('judgementRows.[]', 'onTrackGrades.full')
  get fullGradeLabel() {
    return this._gradeLabel(this.onTrackGrades?.full);
  }

  _gradeLabel(judgement) {
    let row = findBy(this.judgementRows, 'key', judgement);
    return row ? row.label : '';
  }

  /**
   * _row builds one bar: the descriptor spread for one slot of the selected
   * year, and where each line in play (on track, met) falls across it. A half year can
   * hold judgements made under both rules, in which case both marks show.
   *
   * @param {string} half - 'mid' or 'end'.
   */
  _row(half) {
    let year = this.selectedYear;
    let rows = this.judgementRows;
    if (isEmpty(year) || isEmpty(rows)) {
      return null;
    }
    let totalCell = findBy(this.data || [], 'key', 'total').data[year];

    let counts = rows.map((row) => ({
      judgement: row.key,
      label: row.label,
      colour: row.colour,
      textColour: row.textColour,
      count: row.data[year] ? row.data[year][half] : 0,
    }));
    let total = counts.reduce((sum, entry) => sum + entry.count, 0);
    // Counted per learner, against their own Milestone's rule.
    let onTrack = totalCell[`${half}OnTrackCount`];
    let rules = Object.assign({}, totalCell[`${half}Rules`]);
    if (!rules.interim && !rules.full) {
      rules[defaultIsInterim(half) ? 'interim' : 'full'] = true;
    }

    // Everything below a threshold sits to the left, so its mark falls at the
    // cumulative width of the descriptors that do not reach it.
    let marks = [];
    [true, false]
      .filter((isInterim) => rules[isInterim ? 'interim' : 'full'])
      .forEach((isInterim) => {
        let threshold = thresholdFor(isInterim, this.onTrackGrades);
        let below = counts.reduce(
          (sum, entry) =>
            entry.judgement < threshold ? sum + entry.count : sum,
          0,
        );
        let position = total ? (below / total) * 100 : 0;
        let shared = marks.find((mark) => mark.position === position);
        if (shared) {
          shared.name = 'On track and met line';
          shared.isInterim = false;
          return;
        }
        marks.push({
          position,
          isInterim,
          name: isInterim ? 'On track line' : 'Met line',
          grade: this._gradeLabel(threshold),
        });
      });

    return {
      half,
      label: this.labels?.[half],
      mixed: !!(rules.interim && rules.full),
      verb: rulesVerb(rules, half),
      total,
      onTrack,
      percentage: total ? percentOf(onTrack, total) : null,
      marks: marks.map((mark) => ({
        label: `${mark.name}: ${mark.grade} or above`,
        // With both marks on one bar, the interim one is dashed so the two can
        // be told apart.
        dashed: mark.isInterim && marks.length > 1,
        markStyle: htmlSafe(`left: ${mark.position}%;`),
        // The label starts at the mark; placeMarkLabels sets its row, and
        // turns it to end at the mark near the bar's right end.
        labelStyle: htmlSafe(`left: ${mark.position}%;`),
      })),
      segments: barSegments(
        counts.filter((entry) => entry.count > 0),
        total,
      ),
    };
  }

  // The labels are measured where they render, and laid out again whenever
  // the marks or the bar's width change. The observer stops with the bar.
  markLabels = modifier((element, [marks]) => {
    // A label is drawn for each mark, so with no marks there is nothing to place.
    if (!marks?.length) {
      return;
    }
    placeMarkLabels(element);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    let width = element.clientWidth;
    let observer = new ResizeObserver(() => {
      if (element.clientWidth !== width) {
        width = element.clientWidth;
        placeMarkLabels(element);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  @action
  toggleExplanation() {
    this.showExplanation = !this.showExplanation;
  }

  // The drilldown belongs to the page, so the tables and these bars open the
  // same panel.
  @action
  showSegment(bar, segment) {
    this.onSelect(this.selectedYear, bar.half, segment.judgement);
  }
}
